/**
 * Booking a confirmed case, as a deferred action.
 *
 * Same shape as offerEmailAction and moveEmailAction: the board hands it to the
 * undo queue, and nothing happens until the countdown expires. That matters
 * more here than anywhere else — this one call creates a calendar event, writes
 * a Lever interview and emails the candidate, so "Stop" has to mean nothing was
 * ever created.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { RoleBoard, SchedulingCandidate } from '../../types/taActionBoard';
import type { PendingAction } from './usePendingActions';

export function caseBookingAction({
  candidate,
  role,
  start,
  mode,
  bookRoom,
  contactPhone,
}: {
  candidate: SchedulingCandidate;
  role: RoleBoard;
  start: string;
  /** 'onsite' (room + office) or 'remote' (Google Meet); defaults remote server-side. */
  mode?: 'onsite' | 'remote';
  /** Onsite only: when true, the server books the first shortlisted room free for the slot. */
  bookRoom?: boolean;
  /** Onsite only: number the candidate calls on arrival. */
  contactPhone?: string | null;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `case-booking-${candidate.opportunityId}`,
    // See PendingAction.affects — an optimistic marker must not outlive the tab.
    affects: [candidate.opportunityId],
    label: `Booking ${candidate.name}'s case interview`,
    run: async () => {
      try {
        const r = await api.bookCaseInterview({
          postingId: role.postingId,
          opportunityId: candidate.opportunityId,
          roleTitle: role.roleTitle,
          start,
          mode,
          bookRoom: bookRoom || undefined,
          contactPhone: contactPhone || undefined,
        });
        // Reported part by part on purpose: a booking where Lever or the email
        // failed is a REAL state the recruiter has to finish by hand, and a
        // flat "booked" would hide exactly the half that needs them.
        showToast({
          status:
            r.leverCreated && r.emailSent && r.roomBooked !== false && r.feedbackLinkAdded !== false
              ? 'success'
              : 'warning',
          description: [
            `Case booked for ${candidate.name}.`,
            r.emailSent ? 'Candidate emailed.' : 'Candidate NOT emailed — tell them yourself.',
            r.leverCreated ? '' : 'Lever interview NOT created — add it by hand.',
            // The interviewer's own invite is where they reach the scorecard from. If the link
            // never landed, they open the event and find nothing to click — which is how this
            // surfaced in the first place, as a bug report from the interviewer.
            r.leverCreated && r.feedbackLinkAdded === false
              ? 'The feedback form link did not make it onto the invite — tell the panel where to find it.'
              : '',
            r.roomBooked === true ? `Room ${r.roomName} booked.` : '',
            r.roomBooked === false ? 'No free room found — book one by hand.' : '',
            r.rescheduleCapped
              ? 'This panel is longer than self-service reschedule can offer, so they cannot move it themselves.'
              : '',
          ]
            .filter(Boolean)
            .join(' '),
        });
      } catch (e) {
        showToast({
          status: 'error',
          description:
            e instanceof Error && e.message
              ? e.message
              : 'Could not book the case interview — nothing was created.',
        });
      }
    },
    // Nothing has happened yet when the countdown is cancelled, so there is
    // nothing to undo. The tracking row the board wrote optimistically is
    // corrected by the next refresh.
    undo: () => undefined,
  };
}
